import React, { FC, useState } from 'react';
import { Box, CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import { SubmitButton, U1AutocompleteTextField, U1TextField } from 'components/atoms';
import { EmailIcon, PhoneIcon } from 'svgs';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import { isNotPhoneUS, isPhoneInvalid, parsePhoneOnType } from 'utils/phoneHelpers';
import { Field, Form } from 'react-final-form';
import { U1TextFieldProps } from 'components/atoms/U1TextField';
import { U1AutocompleteTextFieldProps } from 'components/atoms/U1AutocompleteTextField';
import { UploaderDrawer } from 'containers/careers';
import { useFormSender } from 'hooks';
import { ValidationErrors } from 'final-form';

export interface IFields {
  name: string;
  label: string;
  additionalInputParams?: Partial<U1TextFieldProps>;
  additionalFieldParams?: unknown;
  additionalAutocompleteInputParams?: Partial<U1AutocompleteTextFieldProps>;
}

interface CareersFormProps {
  jobTitle: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
  },
  span: {
    position: 'absolute',
    right: 15,
    color: theme.palette.error.main,
    top: 261,
    [theme.breakpoints.up('md')]: {
      top: 125,
    },
  },
  spinner: {
    color: theme.palette.common.white,
  },
}));

const validator = ({ email = '', fullName = '', phone = '', yearsOfExperience }) => {
  const errors: Record<string, string> = {};

  // Email
  if (!isEmail(email)) errors.email = 'Invalid Email address';
  if (isEmpty(email)) errors.email = 'Required field';

  //Years Of Experience
  if (isEmpty(yearsOfExperience || '')) errors.yearsOfExperience = 'Required field';

  // Full Name
  if (isEmpty(fullName)) errors.fullName = 'Required field';

  // Phone
  if (isPhoneInvalid(phone)) errors.phone = 'Invalid phone number';
  if (isNotPhoneUS(phone)) errors.phone = 'US numbers Required';

  return errors;
};

const CareersForm: FC<CareersFormProps> = ({ jobTitle }) => {
  const classes = useStyles();
  const [file, setFile] = useState<File>(null);
  const [link, setLink] = useState<string>('');
  const [hasUpload, setHasUpload] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fields: IFields[] = [
    {
      label: 'Full Name*',
      name: 'fullName',
    },
    {
      label: 'Email*',
      name: 'email',
      additionalInputParams: {
        hasIcon: true,
        startIcon: <EmailIcon />,
      },
    },
    {
      label: 'Phone*',
      name: 'phone',
      additionalFieldParams: {
        parse: parsePhoneOnType,
      },
      additionalInputParams: {
        hasIcon: true,
        startIcon: <PhoneIcon />,
      },
    },
    {
      label: (!file || !link) && 'No file chosen.*',
      name: 'noFileChosen',
      additionalInputParams: {
        isUploadFileInput: true,
        onClick: handleClickOpen,
        hasFile: (file || link) && true,
      },
    },
  ];

  const list: IFields[] = [
    {
      label: 'Years of experience*',
      name: 'yearsOfExperience',
      additionalAutocompleteInputParams: {
        listItems: [
          { value: 'intership', label: 'Intership' },
          { value: 'entery-level', label: 'Entery level' },
          { value: 'associate', label: 'Associate' },
          { value: 'mid-senior-level', label: 'Mid-Senior level' },
          { value: 'director', label: 'Director' },
          { value: 'executive', label: 'Executive' },
        ],
      },
    },
  ];

  const message: IFields[] = [
    {
      label: 'Message',
      name: 'message',
      additionalInputParams: {
        multiline: true,
        maxLength: 200,
      },
    },
  ];

  // const onSubmit = async (values) => {
  //   if (!hasUpload && !link) {
  //     setUploadError('Required field');
  //     return;
  //   }
  //
  //   const formData = new FormData();
  //   Object.entries(values).forEach(([key, value]) => formData.set(key, value as string));
  //   formData.set('link', link);
  //   formData.set('file', file);
  //
  //   const response = await fetch('/api/careers', {
  //     method: 'POST',
  //     body: formData,
  //   });
  //
  //   if (response.ok) {
  //     // all good
  //   } else {
  //     // something went wrong on server side
  //   }
  // };

  const onSelectFile = (files: File[]) => {
    setFile(files[0]);
    if (file) {
      setHasUpload(true);
      setUploadError('');
    }
  };

  const onChangeLink = (value: string) => {
    setLink(value);
    if (link) {
      setHasUpload(true);
      setUploadError('');
    }
  };
  const {
    onSubmit,
    status: { error, loading, success },
  } = useFormSender('/api/careers');

  const additionalSubmit = (values, actions) => {
    // @ts-ignore
    if (!file && !link) {
      setUploadError('Required field');
      return;
    } else {
      onSubmit({ file, link, ...values }, actions, () => {
        setLink('');
        setFile(null);
        setUploadError('');
      });
    }
  };

  return (
    <Form
      validate={validator as (values: Record<string, any>) => ValidationErrors | Promise<ValidationErrors>}
      onSubmit={additionalSubmit}
      subscription={{ dirty: true }}
      initialValues={{ foo: `Position - ${jobTitle}` }}
      render={({ handleSubmit, form, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={5} className={classes.root}>
            {fields.map(({ label, name, additionalInputParams, additionalFieldParams }, index) => (
              <Grid item md={6} xs={12} key={index + name} style={{ maxHeight: 75 }}>
                <Field name={name} {...additionalFieldParams}>
                  {({ input, meta: { error, touched } }) => (
                    <>
                      <U1TextField
                        {...input}
                        {...additionalInputParams}
                        disabled={submitting || (name === 'position' && true)}
                        label={label}
                        helperText={touched && error}
                        error={touched && !!error}
                      />
                    </>
                  )}
                </Field>
              </Grid>
            ))}
            <Grid item md={6} xs={12} style={{ maxHeight: 75 }}>
              <Field name={'foo'}>
                {({ input, meta: { error } }) => (
                  <>
                    <U1TextField {...input} helperText={error} error={!!error} value={`Position - ${jobTitle}`} disabled={true} />
                  </>
                )}
              </Field>
            </Grid>
            {(!hasUpload && !link) || submitting ? <span className={classes.span}>{uploadError}</span> : ''}
            {list.map(({ label, name, additionalAutocompleteInputParams, additionalFieldParams }, index) => (
              <Grid item md={6} xs={12} key={index + name}>
                <Field<string[]> name={name} {...additionalFieldParams}>
                  {({ input, meta: { error, touched } }) => (
                    <>
                      <U1AutocompleteTextField
                        {...input}
                        {...additionalAutocompleteInputParams}
                        disabled={submitting || (name === 'position' && true)}
                        label={label}
                        helperText={touched && error}
                        error={touched && !!error}
                      />
                    </>
                  )}
                </Field>
              </Grid>
            ))}
            {message.map(({ label, name, additionalInputParams, additionalFieldParams }, index) => (
              <Grid item xs={12} key={index + name}>
                <Field name={name} {...additionalFieldParams}>
                  {({ input, meta: { error, touched } }) => (
                    <>
                      <U1TextField
                        {...input}
                        {...additionalInputParams}
                        disabled={submitting || (name === 'position' && true)}
                        label={label}
                        helperText={touched && error}
                        error={touched && !!error}
                      />
                    </>
                  )}
                </Field>
              </Grid>
            ))}
          </Grid>
          <Box mt={6} />
          <UploaderDrawer
            open={open}
            onClose={handleClose}
            onSubmit={handleClose}
            onSelectFile={onSelectFile}
            onChangeLink={onChangeLink}
            linkValue={link}
            file={file}
          />
          <SubmitButton disabled={submitting} error={error} success={success}>
            {loading && <CircularProgress size="30px" className={classes.spinner} />}
            {success && 'Thank you for filling out your information!'}
            {error && 'An error occurred. Please try again.'}
            {!loading && !success && !error && 'Send'}
          </SubmitButton>
        </form>
      )}
    />
  );
};

export default CareersForm;
